import React, { useState, useEffect } from 'react';
import TestimonialsSlider from './TestimonialsSlider';
import VisitorCounter from './VisitorCounter';

const Pricing = ({ onPaymentSuccess, autoShowPayment = false }) => {
  const [email, setEmail] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (autoShowPayment) {
      setTimeout(() => {
        const element = document.getElementById('pricing');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [autoShowPayment]);

  const plan = {
    name: 'VowAuthor Package',
    price: 9.99,
    features: [
      'Up to 5 Unique Vow Generations',
      'Use anytime - no expiration',
      'Beautiful, professional formatting',
      'Print-ready elegant PDFs',
      'Perfect for both partners'
    ]
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setProcessing(true);
    setError('');

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setProcessing(false);
      return;
    }

    try {
      try {
        await fetch('/api/abandoned-cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
      } catch (e) {
        console.error('Cart tracking error:', e);
      }

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create checkout session');
      }

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="pricing-section" id="pricing">
      <div className="container py-5">
        <div className="text-center mb-5">
          <h2 className="commercial-heading mb-3">Say "I Do" to Perfect Vows</h2>
          <p className="commercial-subheading">Let's create something beautiful together</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            <div className="pricing-card glass-card">
              {/* Limited Time Offer Badge */}
              <div className="text-center mb-3">
                <span className="badge" style={{
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #1a365d 100%)',
                  color: 'white',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '20px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(30, 58, 95, 0.4)',
                  animation: 'pulse 2s infinite',
                  fontFamily: "'Cormorant Garamond', serif"
                }}>
                  <i className="bi bi-heart-fill me-2"></i>
                  Special Offer: $9.99 for 5 Vow Generations
                </span>
              </div>

              {/* Satisfaction Guarantee */}
              <div className="text-center mb-3">
                <div style={{
                  background: 'rgba(232, 164, 174, 0.15)',
                  border: '2px solid rgba(232, 164, 174, 0.3)',
                  borderRadius: '12px',
                  padding: '0.75rem 1rem',
                  display: 'inline-block'
                }}>
                  <i className="bi bi-shield-check-fill me-2" style={{ color: '#1e3a5f' }}></i>
                  <strong style={{ color: '#5d3a3b' }}>Love It Guarantee:</strong>
                  <span style={{ color: '#5d3a3b', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
                    Not perfect? We'll regenerate for free!
                  </span>
                </div>
              </div>

              <VisitorCounter />

              <h3 className="plan-name text-center">{plan.name}</h3>
              <div className="plan-price text-center">
                <span className="currency">$</span>
                <span className="amount">{plan.price}</span>
              </div>
              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index}>
                    <i className="bi bi-check-circle-fill"></i>
                    {feature}
                  </li>
                ))}
              </ul>
              
              {/* Purchase Process Info */}
              <div className="purchase-process-info mt-4 mb-4">
                <p className="process-description text-center" style={{ fontSize: '0.95rem', color: '#5d3a3b', lineHeight: '1.6' }}>
                  <i className="bi bi-info-circle me-2"></i>
                  After purchase, you'll receive 5 redemption codes via email. 
                  Enter a code, share your love story details, 
                  and we'll instantly craft beautiful, personalized wedding vows.
                </p>
              </div>

              {/* Email Input and Payment Form */}
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label" style={{ color: '#5d3a3b', fontWeight: '600', fontFamily: "'Cormorant Garamond', serif" }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={processing}
                    style={{
                      background: 'rgba(255, 255, 255, 0.7)',
                      border: '1px solid rgba(30, 58, 95, 0.3)',
                      color: '#5d3a3b',
                      borderRadius: '12px',
                      padding: '0.75rem 1rem'
                    }}
                  />
                  <small className="text-muted" style={{ color: '#8b5a5b', opacity: 0.8 }}>
                    Your 5 redemption codes will be sent to this email
                  </small>
                </div>

                {error && (
                  <div className="alert alert-danger mb-3" role="alert">
                    {error}
                  </div>
                )}

                <div className="text-center">
                  <button 
                    type="submit"
                    className="btn btn-primary btn-lg w-100 pulsate-btn btn-with-arrow"
                    disabled={processing}
                  >
                    {processing ? 'Processing...' : 'Continue to Payment'}
                    {!processing && <i className="bi bi-chevron-right chevron-animate ms-2"></i>}
                  </button>
                </div>

                <p className="text-center mt-3 small text-muted" style={{ color: '#8b5a5b', opacity: 0.7 }}>
                  <i className="bi bi-lock-fill me-1"></i>
                  Secure payment powered by Stripe
                </p>
              </form>
            </div>
          </div>
        </div>
        
        {/* Testimonials Slider */}
        <div className="row justify-content-center mt-4">
          <div className="col-lg-8 col-md-10">
            <TestimonialsSlider />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
